/**
 * Maximum number of items to display.
 */
const MAX_VISIBLE_ITEMS = 5;

/**
 * Displays a value or "None" if the value is falsy.
 *
 * @param {string} value - The value to display.
 */
function displayOrNone(value) {
    return value || <span className="italic text-muted-foreground">None</span>;
}

/**
 * Summary view for the carousel editor.
 *
 * @param {Object} data - Non-configurable carousel data.
 * @param {Object} formData - Configurable carousel data.
 * @param {Function} toggleView - Callback function to toggle view.
 */
export default function CarouselEditorSummary({ data, formData, toggleView }) {
    return (
        <div className="grid grid-cols-[max-content_1fr] gap-y-1 gap-x-8 items-baseline">
            <Row label="id">
                <p className="text-sm">{displayOrNone(data.id)}</p>
            </Row>
            {data.displayType === 'PRODUCT_CAROUSEL_1' && (
                <Row label="Background image">
                    {formData.backgroundImageUrl ? (
                        <img
                            alt=""
                            src={
                                formData.backgroundImageUrl.startsWith('http')
                                    ? formData.backgroundImageUrl
                                    : `https://img-cdn.misfitsmarket.com/${formData.backgroundImageUrl}`
                            }
                            className="max-w-60 max-h-15"
                        />
                    ) : (
                        <span className="italic text-muted-foreground">None</span>
                    )}
                </Row>
            )}
            {['PRODUCT_CAROUSEL_2', 'PRODUCT_CAROUSEL_3'].includes(data.displayType) && (
                <Row label="Image">
                    {formData.photoUrlMedium ? (
                        <img alt="" src={formData.photoUrlMedium} className="max-w-60 max-h-15" />
                    ) : (
                        <span className="italic text-muted-foreground">None</span>
                    )}
                </Row>
            )}
            <Row label="Eyebrow">
                <p className="text-sm">{displayOrNone(formData.eyebrow)}</p>
            </Row>
            <Row label="Name">
                <p className="text-sm">{displayOrNone(formData.name)}</p>
            </Row>
            <Row label="Description">
                <p className="text-sm">{displayOrNone(formData.description)}</p>
            </Row>
            <Row label="Link URL">
                <p className="text-sm">{displayOrNone(formData.linkUrl)}</p>
            </Row>
            <Row label="Link Text">
                <p className="text-sm">{displayOrNone(formData.linkText)}</p>
            </Row>
            <Row label="Items">
                {displayOrNone(
                    formData.items.length > 0 && (
                        <ol className="text-sm">
                            {formData.items.slice(0, MAX_VISIBLE_ITEMS).map((item) => (
                                <li key={item.externalSku} className="text-muted-foreground">
                                    {item.externalSku}: {item.name}
                                    <br />
                                </li>
                            ))}
                            {formData.items.length > MAX_VISIBLE_ITEMS && (
                                <li className="text-muted-foreground italic">
                                    <button
                                        className="cursor-pointer hover:underline"
                                        onClick={toggleView}
                                        type="button"
                                    >
                                        +{formData.items.length - MAX_VISIBLE_ITEMS} more products
                                    </button>
                                </li>
                            )}
                        </ol>
                    ),
                )}
            </Row>
        </div>
    );
}

/**
 * Displays a row of label and value.
 *
 * @param {string} label - The label to display.
 * @param {React.ReactNode} children - The value to display.
 * @param {string} labelClass - The class name for the label.
 * @param {string} valueClass - The class name for the value.
 */
function Row({ children, label, labelClass = '', valueClass = '' }) {
    return (
        <>
            <p className={`text-xs font-medium text-muted-foreground ${labelClass}`}>{label}</p>
            <div className={valueClass}>{children}</div>
        </>
    );
}
